import type { RouteLocationNormalizedLoaded } from 'vue-router'

import { useTelemetry } from './useTelemetry'

type RouteTitleMap = Record<string, string>
type TrackPageView = (page: string, properties: Record<string, string>) => void
type RouteForAnalytics = Pick<RouteLocationNormalizedLoaded, 'fullPath' | 'matched' | 'path'>

interface RoutePageViewTrackerOptions {
  getDocumentTitle?: () => string
  getOrigin?: () => string
  trackPageView?: TrackPageView
}

const DEFAULT_PAGE_TITLE = 'Kaizen Platform'

const getDefaultOrigin = (): string => {
  if (typeof window === 'undefined') return ''
  return window.location.origin
}

const getDefaultDocumentTitle = (): string => {
  if (typeof document === 'undefined') return ''
  return document.title
}

const getRouteName = (route: RouteLocationNormalizedLoaded): string | undefined => {
  return typeof route.name === 'string' ? route.name : undefined
}

export const getFrontendRoute = (route: RouteForAnalytics): string => {
  const matchedRoute = route.matched[route.matched.length - 1]
  if (matchedRoute?.path) return matchedRoute.path
  if (route.path) return route.path
  return route.fullPath.split(/[?#]/)[0] || '/'
}

export const createRoutePageViewTracker = (options: RoutePageViewTrackerOptions = {}) => {
  const trackPageView = options.trackPageView ?? useTelemetry().trackPageView
  const getOrigin = options.getOrigin ?? getDefaultOrigin
  const getDocumentTitle = options.getDocumentTitle ?? getDefaultDocumentTitle
  let hasSkippedInitialPageView = false

  return (to: RouteLocationNormalizedLoaded, routeTitleMap: RouteTitleMap) => {
    if (!hasSkippedInitialPageView) {
      hasSkippedInitialPageView = true
      return
    }

    const pagePath = to.fullPath || to.path || '/'
    const origin = getOrigin()
    const routeName = getRouteName(to)
    const routeTitle = routeName ? routeTitleMap[routeName] : ''
    const pageTitle = routeTitle || getDocumentTitle() || DEFAULT_PAGE_TITLE
    const frontendRoute = getFrontendRoute(to)
    const basePageParams: Record<string, string> = {
      frontend_route: frontendRoute,
      page_location: origin ? `${origin}${pagePath}` : pagePath,
      page_title: pageTitle,
    }
    const pageParams = routeName ? { ...basePageParams, route_name: routeName } : basePageParams

    trackPageView(pagePath, pageParams)
  }
}
